import { redirectTo } from "../navigation/navigation.ts";
import axios from "axios";
import type { PlatformPage, PlatformResource } from "./platformAdminContract.ts";

const client = axios.create({
  baseURL: process.env.NODE_ENV === "development"
    ? "/admin/api"
    : (process.env.REACT_APP_Server_Url || "").replace(/\/+$/, ""),
  withCredentials: true,
});

client.interceptors.response.use(undefined, function (error) {
  if (error?.response?.status === 401 && window.location.pathname.startsWith("/admin")
      && error.config?.url !== "/identity/login") {
    redirectTo("/admin/login");
  }
  return Promise.reject(error);
});

export type PlatformIdentity = {
  accountKind: "platform" | "customer";
  userId: string | null;
  isAuthenticated: boolean;
  userName: string | null;
  csrfToken: string;
};

export async function emailHistory(page: number, search: string, signal: AbortSignal) {
  return (await client.get("/platform/emails", { params: { page, pageSize: 1000, search }, signal })).data;
}
export async function emailAttempts(id: string, signal: AbortSignal) {
  return (await client.get("/platform/emails/" + id + "/attempts", { signal })).data;
}

export async function getPlatformIdentity(): Promise<PlatformIdentity> {
  const response = await client.get<PlatformIdentity>("/identity/context");
  return response.data;
}

export async function platformPreferenceRequest(
  method: "get" | "put" | "delete", url: string, data: unknown, csrfToken: string, signal?: AbortSignal,
): Promise<unknown> {
  const response = await client.request({ method, url, data, signal, headers: { "X-Portal-Csrf": csrfToken } });
  return response.status === 204 ? null : response.data;
}

export async function logoutPlatform(csrfToken: string): Promise<void> {
  await client.post("/identity/logout", undefined, {
    headers: { "X-Portal-Csrf": csrfToken },
  });
}

export async function loginPlatform(userName: string, password: string, csrfToken: string): Promise<{
  accountKind: "platform" | "customer";
}> {
  const response = await client.post("/identity/login", { userName: userName.trim(), password }, {
    headers: { "X-Portal-Csrf": csrfToken },
  });
  return response.data;
}

export async function listPlatformRecords(
  resource: PlatformResource,
  query: { page: number; pageSize: number; all: boolean; companyId?: number; search?: string },
  signal?: AbortSignal,
): Promise<PlatformPage> {
  const response = await client.get<PlatformPage>(`/platform/${resource}`, {
    params: query,
    signal,
  });
  return response.data;
}

export async function executePlatformOperation(
  path: string,
  body: Record<string, unknown>,
  csrfToken: string,
): Promise<void> {
  await client.post(path, body, {
    headers: { "X-Portal-Csrf": csrfToken },
  });
}

export async function getPlatformGuide(id: number): Promise<import("./platformAdminContract.ts").PlatformRecord> {
  const response = await client.get(`/platform/guides/${id}`);
  return response.data;
}
